import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoginPage from '../features/Auth/LoginPage';
import RegisterPage from '../features/Auth/RegisterPage';
import DashboardPage from '../features/Dashboard/DashboardPage';
import ATSPage from '../features/ATS/ATSPage';
import AdminPage from '../features/AdminPanel/AdminPage';
import BillingPage from '../features/Billing/BillingPage';
import './App.css';
import { supabase } from '../lib/supabaseClient';
import { setUserDetails, setSession, clearAuth, setLoading } from '../store/slices/authSlice';
import { getUserDetailsWithEdgeFunction } from '../features/Auth/api/getUserDetailsWithEdgeFunction';
import { getEmployerNotifications } from '../lib/notifications/getEmployerNotifications';
import { selectSession } from '../store/selectors/authSelectors';
import { SnackbarProvider } from '../contexts/SnackbarContext';
import { Notification } from '../types/notifications';
import NotificationsModal from '../components/NotificationsModal/NotificationsModal';
import GlobalLoader from '../components/GlobalLoader/GlobalLoader';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import { AppDispatch } from '../store/store';


// Protected Route component
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const session = useSelector(selectSession);
  const isAuthenticated = !!session;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const session = useSelector(selectSession);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!isMounted) return;
      setIsAuthenticated(!!session);
      if (session) {
        dispatch(setSession(session));
      }
    };

    initSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        dispatch(setSession(session));
      } else {
        // Clear auth state when logged out
        dispatch(clearAuth());
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [dispatch]);

  // useEffect to watch isAuthenticated and fetch user details when it becomes true
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (isAuthenticated && session?.user) {
        dispatch(setLoading(true));
        const response = await getUserDetailsWithEdgeFunction(session.user.id);
        if (response.data && !response.error) {
          dispatch(setUserDetails(response.data));
        } else {
          console.error('Failed to fetch user details:', response.error);
        }
        dispatch(setLoading(false));
      }
    };

    fetchUserDetails();
  }, [isAuthenticated, session, dispatch]);

  // useEffect to fetch notifications after user details are fetched
  useEffect(() => {
    const fetchNotifications = async () => {
      if (isAuthenticated && session?.user) {
        try {
          const response = await getEmployerNotifications();
          if (response.data && !response.error && response.data.notifications) {
            const notificationList = response.data.notifications || [];
            if (notificationList.length > 0) {
              setNotifications(notificationList);
              setIsNotificationsModalOpen(true);
            }
          } else if (response.error) {
            // Log error but don't block login flow
            console.error('Failed to fetch notifications:', response.error);
          }
        } catch (err) {
          // Log error but don't block login flow
          console.error('Error fetching notifications:', err);
        }
      }
    };

    // Fetch notifications after a short delay to ensure user details are loaded
    // This ensures notifications modal appears after the user is fully logged in
    const timer = setTimeout(() => {
      fetchNotifications();
    }, 500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, session]);

  const handleLogin = () => {
    // No-op: onAuthStateChange drives state after OTP verification
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    dispatch(clearAuth());
    setNotifications([]);
    setIsNotificationsModalOpen(false);
  };

  const handleCloseNotificationsModal = () => {
    setIsNotificationsModalOpen(false);
  };

  return (
    <SnackbarProvider>
      <ErrorBoundary>
        <div className="App">
          <NotificationsModal
            isOpen={isNotificationsModalOpen}
            notifications={notifications}
            onClose={handleCloseNotificationsModal}
          />
          <GlobalLoader />
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <LoginPage onLoginSuccess={handleLogin} />
                )
              }
            />

            <Route
              path="/register"
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <RegisterPage />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ats/:jobId"
              element={
                <ProtectedRoute>
                  <ATSPage user={session?.user || null} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage user={session?.user || null} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <BillingPage user={session?.user || null} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />} />
          </Routes>
        </div>
      </ErrorBoundary>
    </SnackbarProvider>
  );
}

export default App;
